import { FunctionComponent, useEffect, useRef, useState } from 'react'

import styled, { css } from 'styled-components'

import { getCurrentViewportType } from '@measures/responsive'
import translate from '@i18n'
import {
  SportEntityScheduleAPIResponse,
  SportEntityScheduleItem,
} from '@hooks/useSportEntitySchedule'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import Tile from '@components/RelatedMatches/Tile'

import { getMostRecentItems, isMostRecentItem } from './utils'

export interface SportEntityScheduleCompetitorProps {
  items?: SportEntityScheduleAPIResponse['items']
}

const ShowAllGamesButton = styled(PrimaryCTAButton)`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    padding: ${spacing16};
  `}
`

const OuterWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing24 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing24};
    align-items: center;
  `}
`

const SportEntityScheduleWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing16};
    width: 100%;
  `}
`
const SportEntityScheduleGameWrapper = styled.div`
  width: 100%;
  > * {
    width: 100%;
  }
`

const SportEntityScheduleComponent: FunctionComponent<
  SportEntityScheduleCompetitorProps
> = ({ items }) => {
  const [showAllGames, setShowAllGames] = useState(false)
  const [scheduleItems, setScheduleItems] = useState<SportEntityScheduleItem[]>(
    []
  )
  const ref = useRef<HTMLDivElement>(null)
  const shouldShowMoreGamesButton = !showAllGames && items && items.length > 5

  const onClick = () => {
    const isDesktop = getCurrentViewportType() === 'desktop'
    setShowAllGames(true)
    setScheduleItems(items || [])

    if (isDesktop) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    const limitedItems = getMostRecentItems(items || [])
    setScheduleItems(shouldShowMoreGamesButton ? limitedItems : items || [])
  }, [items, shouldShowMoreGamesButton])

  return (
    <OuterWrapper>
      <SportEntityScheduleWrapper>
        {scheduleItems.map((item) => {
          return (
            <SportEntityScheduleGameWrapper
              key={`${item.typeOfSport}-${item.matchId}`}
              ref={isMostRecentItem(scheduleItems, item.matchId) ? ref : null}>
              <Tile
                matchId={item.matchId}
                typeOfSport={item.typeOfSport}
                link={item.link}
              />
            </SportEntityScheduleGameWrapper>
          )
        })}
      </SportEntityScheduleWrapper>
      {shouldShowMoreGamesButton && (
        <ShowAllGamesButton
          colorVariant={'black'}
          size="small"
          onClick={onClick}>
          {translate('sportEntitySchedule.buttonText')}
        </ShowAllGamesButton>
      )}
    </OuterWrapper>
  )
}

export default SportEntityScheduleComponent
